/**
 * PrivacyPolicy - Política de Privacidade da plataforma HeveaTalent
 */
import React from "react";
import { useTranslation } from "../translations";
import { ArrowLeft } from "lucide-react";

const PrivacyPolicy = () => {
  const { t } = useTranslation();
  
  return (
    <div className="min-h-screen bg-white" data-testid="privacy-page">
      <header className="bg-green-800 text-white py-8">
        <div className="container mx-auto px-4">
          <a href="/" className="inline-flex items-center gap-2 text-green-200 hover:text-white mb-4 text-sm">
            <ArrowLeft className="w-4 h-4" /> HeveaTalent
          </a>
          <h1 className="text-3xl font-bold">{t('privacyTitle')}</h1>
          <p className="text-green-200 mt-2">{t('lastUpdated')}: 17/03/2026</p>
        </div>
      </header>

      <main className="container mx-auto px-4 py-12 max-w-4xl">
        <div className="prose prose-lg max-w-none text-gray-700 space-y-8">
          
          <section>
            <h2 className="text-2xl font-bold text-gray-900">1. Introdução</h2>
            <p>A HeveaTalent ("nós") está comprometida com a proteção da sua privacidade. Esta Política de Privacidade explica como coletamos, usamos, armazenamos e protegemos suas informações pessoais em conformidade com a Lei Geral de Proteção de Dados (LGPD - Lei 13.709/2018).</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900">2. Dados Coletados</h2>
            <p>Coletamos os seguintes dados:</p>
            
            <h3 className="text-xl font-semibold text-gray-800 mt-4">Dados de Cadastro:</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>Nome completo</li>
              <li>Email</li>
              <li>Telefone (opcional)</li>
              <li>Tipo de perfil (profissional/proprietário)</li>
              <li>Localização (cidade/estado)</li>
            </ul>
            
            <h3 className="text-xl font-semibold text-gray-800 mt-4">Dados de Uso:</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>Registros de produção e sangria</li>
              <li>Avaliações de desempenho</li>
              <li>Interações na comunidade</li>
              <li>Histórico de navegação na plataforma</li>
            </ul>
            
            <h3 className="text-xl font-semibold text-gray-800 mt-4">Dados de Pagamento:</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>Processados diretamente pelo Stripe (não armazenamos dados de cartão)</li>
              <li>Histórico de transações e assinaturas</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900">3. Como Usamos seus Dados</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Prestação do serviço:</strong> Gerenciar sua conta, processar pagamentos, fornecer relatórios</li>
              <li><strong>Melhoria da plataforma:</strong> Análise de uso para aprimorar funcionalidades</li>
              <li><strong>Comunicação:</strong> Notificações sobre sua conta, atualizações da plataforma</li>
              <li><strong>Segurança:</strong> Prevenção de fraudes e atividades suspeitas</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900">4. Compartilhamento de Dados</h2>
            <p>Seus dados podem ser compartilhados com:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Stripe:</strong> Para processamento de pagamentos (regido pela política de privacidade do Stripe)</li>
              <li><strong>Outros usuários:</strong> Informações de perfil público conforme configurações de privacidade</li>
              <li><strong>Autoridades legais:</strong> Quando exigido por lei ou ordem judicial</li>
            </ul>
            <p className="font-semibold mt-2">Nunca vendemos seus dados pessoais para terceiros.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900">5. Armazenamento e Segurança</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>Dados armazenados em servidores seguros com criptografia</li>
              <li>Senhas são armazenadas com hash bcrypt (nunca em texto puro)</li>
              <li>Comunicação via HTTPS (SSL/TLS)</li>
              <li>Acesso restrito a funcionários autorizados</li>
              <li>Backups regulares para prevenção de perda de dados</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900">6. Seus Direitos (LGPD)</h2>
            <p>Conforme a LGPD, você tem direito a:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Acesso:</strong> Solicitar cópia dos seus dados pessoais</li>
              <li><strong>Correção:</strong> Corrigir dados incompletos ou desatualizados</li>
              <li><strong>Exclusão:</strong> Solicitar a exclusão dos seus dados</li>
              <li><strong>Portabilidade:</strong> Receber seus dados em formato estruturado</li>
              <li><strong>Revogação:</strong> Retirar consentimento a qualquer momento</li>
              <li><strong>Oposição:</strong> Opor-se ao tratamento de dados em certas situações</li>
            </ul>
            <p className="mt-2">Para exercer seus direitos, entre em contato: <strong>heveatalent@gmail.com</strong></p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900">7. Cookies</h2>
            <p>Utilizamos cookies e tecnologias similares para:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Manter sua sessão de login ativa</li>
              <li>Lembrar preferências de idioma</li>
              <li>Analisar o uso da plataforma</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900">8. Menores de Idade</h2>
            <p>A plataforma não é destinada a menores de 18 anos. Não coletamos intencionalmente dados de menores.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900">9. Alterações nesta Política</h2>
            <p>Podemos atualizar esta Política periodicamente. Notificaremos sobre alterações significativas por email ou notificação na plataforma.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900">10. Contato</h2>
            <p>Para questões sobre privacidade e proteção de dados:</p>
            <p className="font-semibold">Email: heveatalent@gmail.com</p>
            <p className="text-sm text-gray-500 mt-2">Responsável pelo tratamento de dados: HeveaTalent</p>
          </section>

        </div>
      </main>

      <footer className="py-8 bg-gray-900">
        <div className="container mx-auto px-4 text-center">
          <div className="flex justify-center gap-6 text-sm text-gray-400 mb-3">
            <a href="/proposito" className="hover:text-green-400 transition-colors">HeveaTalent</a>
            <a href="/termos" className="hover:text-green-400 transition-colors">{t('termsTitle') || 'Termos de Uso'}</a>
          </div>
          <p className="text-gray-500 text-xs">© 2025-2026 HeveaTalent</p>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
